import React, { useState } from 'react';
import { ChatWidget } from '@/components/chat/ChatWidget';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Code, Copy, Check } from 'lucide-react';
import type { ChatConfig } from '@/types/chat';

export default function ChatDemo() {
  const [config, setConfig] = useState<ChatConfig>({
    apiKey: 'demo-api-key',
    apiEndpoint: 'http://localhost:5000/api',
    theme: 'light',
    position: 'bottom-right',
    initialOpen: false,
    placeholder: 'Type your message...',
    welcomeMessage: 'Hi! How can I help you today?',
    primaryColor: '#0066FF',
    fontFamily: 'Inter, sans-serif',
    borderRadius: '12px',
    enableTypingIndicator: true,
    enableSoundNotifications: false,
    maxMessageLength: 1000,
    metadata: {
      page: 'demo',
    },
  });

  const [copied, setCopied] = useState(false);

  const handleConfigChange = (key: keyof ChatConfig, value: any) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  const generateEmbedCode = () => {
    return `<script 
  src="https://cdn.your-domain.com/chat-widget.js" 
  data-api-key="${config.apiKey}"
  data-theme="${config.theme}"
  data-position="${config.position}"
  data-primary-color="${config.primaryColor}"
></script>`;
  };

  const generateReactCode = () => {
    return `import { ChatWidget } from '@your-org/chat-widget';
import '@your-org/chat-widget/dist/style.css';

function App() {
  return (
    <ChatWidget 
      apiKey="${config.apiKey}"
      theme="${config.theme}"
      position="${config.position}"
      primaryColor="${config.primaryColor}"
      welcomeMessage="${config.welcomeMessage}"
    />
  );
}`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
            Embeddable AI Chat Widget
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            A powerful, customizable conversational AI chat interface that can be embedded anywhere.
            Try it out and customize it to match your brand!
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Configuration Panel */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle>Configuration</CardTitle>
                <CardDescription>Customize the chat widget appearance and behavior</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* API Key */}
                <div className="space-y-2">
                  <Label htmlFor="apiKey">API Key</Label>
                  <Input
                    id="apiKey"
                    value={config.apiKey}
                    onChange={(e) => handleConfigChange('apiKey', e.target.value)}
                    placeholder="your-api-key"
                  />
                </div>

                {/* Theme */}
                <div className="space-y-2">
                  <Label htmlFor="theme">Theme</Label>
                  <Select
                    value={config.theme}
                    onValueChange={(value) => handleConfigChange('theme', value)}
                  >
                    <SelectTrigger id="theme">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="light">Light</SelectItem>
                      <SelectItem value="dark">Dark</SelectItem>
                      <SelectItem value="auto">Auto</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Position */}
                <div className="space-y-2">
                  <Label htmlFor="position">Position</Label>
                  <Select
                    value={config.position}
                    onValueChange={(value) => handleConfigChange('position', value)}
                  >
                    <SelectTrigger id="position">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bottom-right">Bottom Right</SelectItem>
                      <SelectItem value="bottom-left">Bottom Left</SelectItem>
                      <SelectItem value="top-right">Top Right</SelectItem>
                      <SelectItem value="top-left">Top Left</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Primary Color */}
                <div className="space-y-2">
                  <Label htmlFor="primaryColor">Primary Color</Label>
                  <div className="flex gap-2">
                    <Input
                      id="primaryColor"
                      type="color"
                      value={config.primaryColor}
                      onChange={(e) => handleConfigChange('primaryColor', e.target.value)}
                      className="w-20 h-10"
                    />
                    <Input
                      value={config.primaryColor}
                      onChange={(e) => handleConfigChange('primaryColor', e.target.value)}
                      placeholder="#0066FF"
                    />
                  </div>
                </div>

                {/* Welcome Message */}
                <div className="space-y-2">
                  <Label htmlFor="welcomeMessage">Welcome Message</Label>
                  <Input
                    id="welcomeMessage"
                    value={config.welcomeMessage}
                    onChange={(e) => handleConfigChange('welcomeMessage', e.target.value)}
                    placeholder="Hi! How can I help you today?"
                  />
                </div>

                {/* Placeholder */}
                <div className="space-y-2">
                  <Label htmlFor="placeholder">Input Placeholder</Label>
                  <Input
                    id="placeholder"
                    value={config.placeholder}
                    onChange={(e) => handleConfigChange('placeholder', e.target.value)}
                    placeholder="Type your message..."
                  />
                </div>

                {/* Initial Open */}
                <div className="flex items-center justify-between">
                  <Label htmlFor="initialOpen">Open by Default</Label>
                  <Switch
                    id="initialOpen"
                    checked={config.initialOpen}
                    onCheckedChange={(checked) => handleConfigChange('initialOpen', checked)}
                  />
                </div>

                {/* Typing Indicator */}
                <div className="flex items-center justify-between">
                  <Label htmlFor="typingIndicator">Typing Indicator</Label>
                  <Switch
                    id="typingIndicator"
                    checked={config.enableTypingIndicator}
                    onCheckedChange={(checked) =>
                      handleConfigChange('enableTypingIndicator', checked)
                    }
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Integration Code & Features */}
          <div className="lg:col-span-2 space-y-6">
            {/* Integration Code */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Code className="w-5 h-5" />
                  Integration Code
                </CardTitle>
                <CardDescription>
                  Copy and paste this code to embed the chat widget in your application
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="html">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="html">HTML</TabsTrigger>
                    <TabsTrigger value="react">React</TabsTrigger>
                    <TabsTrigger value="vue">Vue</TabsTrigger>
                  </TabsList>

                  <TabsContent value="html" className="space-y-4">
                    <div className="relative">
                      <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm">
                        <code>{generateEmbedCode()}</code>
                      </pre>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="absolute top-2 right-2"
                        onClick={() => copyToClipboard(generateEmbedCode())}
                      >
                        {copied ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </TabsContent>

                  <TabsContent value="react" className="space-y-4">
                    <div className="relative">
                      <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm">
                        <code>{generateReactCode()}</code>
                      </pre>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="absolute top-2 right-2"
                        onClick={() => copyToClipboard(generateReactCode())}
                      >
                        {copied ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </TabsContent>

                  <TabsContent value="vue" className="space-y-4">
                    <div className="relative">
                      <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm">
                        <code>{`<template>
  <ChatWidget 
    :api-key="${config.apiKey}"
    theme="${config.theme}"
    position="${config.position}"
  />
</template>

<script setup>
import { ChatWidget } from '@your-org/chat-widget';
</script>`}</code>
                      </pre>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="absolute top-2 right-2"
                        onClick={() => copyToClipboard(generateReactCode())}
                      >
                        {copied ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>

            {/* Features */}
            <Card>
              <CardHeader>
                <CardTitle>Key Features</CardTitle>
                <CardDescription>
                  Everything you need for a powerful conversational AI experience
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    {
                      title: 'Real-time Communication',
                      description: 'WebSocket-based instant messaging with typing indicators',
                    },
                    {
                      title: 'Context-Aware AI',
                      description: 'Maintains conversation context across multiple exchanges',
                    },
                    {
                      title: 'Fully Customizable',
                      description: 'Match your brand with custom colors, fonts, and positioning',
                    },
                    {
                      title: 'Easy Integration',
                      description: 'Embed with a single line of code in any application',
                    },
                    {
                      title: 'Session Management',
                      description: 'Persistent conversations across page loads',
                    },
                    {
                      title: 'Responsive Design',
                      description: 'Works perfectly on desktop, tablet, and mobile devices',
                    },
                    {
                      title: 'Accessibility',
                      description: 'WCAG 2.1 AA compliant with keyboard navigation',
                    },
                    {
                      title: 'Error Handling',
                      description: 'Graceful degradation with retry mechanisms',
                    },
                  ].map((feature, index) => (
                    <div
                      key={index}
                      className="p-4 rounded-lg border bg-card hover:shadow-md transition-shadow"
                    >
                      <h4 className="font-semibold mb-1">{feature.title}</h4>
                      <p className="text-sm text-muted-foreground">{feature.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Live Demo Section */}
        <div className="mt-12 text-center">
          <div className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium mb-4">
            👉 Look at the bottom-right corner to see the live widget!
          </div>
          <p className="text-muted-foreground">
            The chat widget is currently active on this page. Click the button to start chatting!
          </p>
        </div>
      </div>

      {/* Chat Widget */}
      <ChatWidget config={config} />
    </div>
  );
}
